import { WizardFieldOrigin } from './types'

/**
 * Origin → the glyph and colour the rail's legend explains. Kept in one place
 * so a field, its legend entry and the rail cannot drift apart.
 */
export const FIELD_ORIGIN_LABEL: Record<WizardFieldOrigin, string> = {
  [WizardFieldOrigin.READ]: 'Read from your message',
  [WizardFieldOrigin.REQUIRED]: 'Required — Allposit will not guess this',
  [WizardFieldOrigin.DEFAULT]: 'Repository default, editable',
  [WizardFieldOrigin.OPTIONAL]: 'Optional',
  // Not in the rail's legend: a fixed value has no glyph to explain.
  [WizardFieldOrigin.FIXED]: 'Set by this wizard',
}

/** The step every wizard ends on, and the one the assistant opens it at. */
export const SUMMARY_STEP_KEY = 'summary'

/** The tags step, which every wizard carries and none of them validates. */
export const TAGS_STEP_KEY = 'tags'

/** The step that records what the asset is worth. Optional wherever it appears. */
export const VALUE_STEP_KEY = 'value'

/**
 * The recorded values, in form state. Named for the rows they become — one
 * `asset_prices` row each — rather than for the step that collects them.
 */
export const MARKS_FIELD = 'prices'

/**
 * The field every flow names its asset in. One constant because the read can
 * fill it from a stated value *or* from a name the sentence only supported —
 * see `aiFilledFields` — and a string spelled in two places is a mark that
 * silently stops appearing.
 */
export const ASSET_NAME_FIELD = 'assetName'

/**
 * The field naming the account a holding is *held in* — a brokerage, a lender —
 * which only the flows that keep their own account step collect. Paired with
 * `accountId`: see `holdsAccount`.
 */
export const ACCOUNT_NAME_FIELD = 'accountName'

/** The step that asks which account a record hangs off. */
export const OWNERSHIP_STEP_KEY = 'ownership'

/**
 * The step that splits a commitment across more than one investing entity —
 * private_fund_subscribe_and_commit's own, in place of the ownership step
 * every other flow keeps. See `investorsStepOf`.
 */
export const INVESTORS_STEP_KEY = 'investors'

/**
 * The step that lists more than one entity each independently holding the
 * same fund position — private_fund's own, in place of the single ownership
 * step every other single-owner flow keeps. Unlike
 * `INVESTORS_STEP_KEY`/private_fund_subscribe_and_commit, there is no split
 * to size a row by: each entity becomes its own full `add position` record
 * for the same amount, never a share of one commitment. Kept as its own key
 * — not `INVESTORS_STEP_KEY` — so the rail's own share/sum rule for that
 * other step never applies here. See `entitiesStepOf`.
 */
export const ENTITIES_STEP_KEY = 'entities'

/**
 * The field the shared ownership step is recognised by. Two flows name a step
 * of their own `ownership` while collecting a held-in account instead, so the
 * key alone does not say whether a wizard asks the ownership question — see
 * `ownershipStepOf`.
 */
export const OWNER_ACCOUNT_ID_FIELD = 'ownerAccountId'

/**
 * Which half of the ownership step is in play. Form state rather than component
 * state: the step is stateless, and the schema branches on it.
 */
export const ACCOUNT_MODE_FIELD = 'ownerAccountMode'

/**
 * Which half of real_estate_with_financing's own mortgage step is in play —
 * the same existing/new choice as `ACCOUNT_MODE_FIELD`, kept as its own field
 * because a financed property already has an ownership step of its own: one
 * mode field cannot answer both "who owns this" and "which mortgage is this".
 */
export const MORTGAGE_ACCOUNT_MODE_FIELD = 'mortgageAccountMode'

/** The step that logs capital calls, fees and distributions against a fund's own investors. */
export const CASH_FLOWS_STEP_KEY = 'cashFlows'

/** The recorded cash flows, in form state — see WizardCashFlow. */
export const CASH_FLOWS_FIELD = 'cashFlows'

/**
 * A cash flow's own type list, grouped by direction — the same split the
 * legacy wizard's Contributions/Distributions steps drew as two screens,
 * merged here into one dropdown so the direction is a property of the row
 * rather than of which step the user happened to be on. Shared rather than
 * kept with private_fund's own step: the summary reads it back too, and a
 * step built for a second flow would want the same vocabulary rather than
 * inventing its own.
 */
export const CONTRIBUTION_TYPES = ['capital call', 'asset fees']

export const DISTRIBUTION_TYPES = [
  'capital return',
  'recallable capital return',
  'cash dividend',
  'cash interest',
  'current income',
  'realized gain or loss',
]

/** How each type reads on screen — the repository's own string, sentence-cased. */
export const CASH_FLOW_TYPE_LABEL: Record<string, string> = {
  'capital call': 'Capital call',
  'asset fees': 'Asset fees',
  'capital return': 'Capital return',
  'recallable capital return': 'Recallable capital return',
  'cash dividend': 'Cash dividend',
  'cash interest': 'Cash interest',
  'current income': 'Current income',
  'realized gain or loss': 'Realized gain or loss',
}

